import { Building } from '../types/building';
import { NonCompliantBuilding, ComplianceData, Violation, ComplianceFilters, ComplianceSortOptions } from '../types/compliance';
import { RetrofitRecommendation, RetrofitCategory } from '../types/retrofit';

export class BuildingAnalysisService {
  private static readonly EMISSION_LIMITS = {
    2024: 0.00596, // metric tons CO2e per square foot
    2030: 0.00453,
    2035: 0.00322,
  };

  private static readonly ENERGY_BENCHMARK = 50; // kBtu per square foot

  /**
   * Analyze building compliance with Local Law 97
   */
  static analyzeCompliance(building: Building): ComplianceData {
    const currentYear = new Date().getFullYear();
    const applicableLimit = this.getApplicableEmissionLimit(currentYear);
    
    const carbonEmissions = this.calculateCarbonEmissions(building);
    const energyIntensity = this.calculateEnergyIntensity(building);
    
    const violations = this.detectViolations(building, carbonEmissions, energyIntensity, applicableLimit);
    const isCompliant = violations.length === 0;
    const complianceScore = this.calculateComplianceScore(building, carbonEmissions, energyIntensity, applicableLimit);

    return {
      buildingId: building.id,
      isCompliant,
      complianceScore,
      violations,
      nextDeadline: this.getNextDeadline(currentYear),
      estimatedPenalty: this.calculatePenalty(building, violations),
      lastAssessment: new Date().toISOString(),
      carbonEmissions: {
        current: carbonEmissions,
        limit: applicableLimit,
        unit: 'metric tons CO2e per sq ft'
      },
      energyIntensity: {
        current: energyIntensity,
        benchmark: this.ENERGY_BENCHMARK,
        unit: 'kBtu per sq ft'
      }
    };
  }

  /**
   * Get non-compliant buildings with analysis
   */
  static async getNonCompliantBuildings(buildings: Building[]): Promise<NonCompliantBuilding[]> {
    const nonCompliantBuildings: NonCompliantBuilding[] = [];

    for (const building of buildings) {
      const complianceData = this.analyzeCompliance(building);
      
      if (!complianceData.isCompliant) {
        const retrofitRecommendations = this.generateRetrofitRecommendations(building, complianceData);
        const priority = this.calculatePriority(complianceData);
        const { estimatedCost, potentialSavings, paybackPeriod } = this.calculateFinancials(retrofitRecommendations);

        nonCompliantBuildings.push({
          ...building,
          complianceData,
          retrofitRecommendations,
          priority,
          estimatedCost,
          potentialSavings,
          paybackPeriod
        });
      }
    }

    return nonCompliantBuildings;
  }

  /**
   * Filter non-compliant buildings
   */
  static filterBuildings(buildings: NonCompliantBuilding[], filters: ComplianceFilters): NonCompliantBuilding[] {
    return buildings.filter(building => {
      if (filters.violationType && !building.complianceData.violations.some(v => v.type === filters.violationType)) {
        return false;
      }

      if (filters.severity && !building.complianceData.violations.some(v => v.severity === filters.severity)) {
        return false;
      }

      if (filters.priority && building.priority !== filters.priority) {
        return false;
      }

      if (filters.costRange) {
        if (building.estimatedCost < filters.costRange.min || building.estimatedCost > filters.costRange.max) {
          return false;
        }
      }

      if (filters.paybackPeriod) {
        if (building.paybackPeriod < filters.paybackPeriod.min || building.paybackPeriod > filters.paybackPeriod.max) {
          return false;
        }
      }

      if (filters.buildingType && building.buildingType !== filters.buildingType) {
        return false;
      }

      if (filters.borough && building.borough !== filters.borough) {
        return false;
      }

      return true;
    });
  }

  /**
   * Sort non-compliant buildings
   */
  static sortBuildings(buildings: NonCompliantBuilding[], sortOptions: ComplianceSortOptions): NonCompliantBuilding[] {
    return [...buildings].sort((a, b) => {
      let valueA: number | string;
      let valueB: number | string;

      switch (sortOptions.field) {
        case 'complianceScore':
          valueA = a.complianceData.complianceScore;
          valueB = b.complianceData.complianceScore;
          break;
        case 'estimatedCost':
          valueA = a.estimatedCost;
          valueB = b.estimatedCost;
          break;
        case 'potentialSavings':
          valueA = a.potentialSavings;
          valueB = b.potentialSavings;
          break;
        case 'paybackPeriod':
          valueA = a.paybackPeriod;
          valueB = b.paybackPeriod;
          break;
        case 'nextDeadline':
          valueA = a.complianceData.nextDeadline || '9999-12-31';
          valueB = b.complianceData.nextDeadline || '9999-12-31';
          break;
        case 'carbonEmissions':
          valueA = a.complianceData.carbonEmissions.current;
          valueB = b.complianceData.carbonEmissions.current;
          break;
        default:
          return 0;
      }

      if (typeof valueA === 'string' && typeof valueB === 'string') {
        return sortOptions.direction === 'asc' 
          ? valueA.localeCompare(valueB)
          : valueB.localeCompare(valueA);
      }

      const numA = valueA as number;
      const numB = valueB as number;
      
      return sortOptions.direction === 'asc' ? numA - numB : numB - numA;
    });
  }

  private static getApplicableEmissionLimit(year: number): number {
    if (year >= 2035) return this.EMISSION_LIMITS[2035];
    if (year >= 2030) return this.EMISSION_LIMITS[2030];
    return this.EMISSION_LIMITS[2024];
  }

  private static calculateCarbonEmissions(building: Building): number {
    // Simplified calculation - in real implementation, use actual energy data
    const baseEmission = 0.005;
    const typeMultiplier = building.buildingType === 'office' ? 1.2 : 
                          building.buildingType === 'residential' ? 0.8 : 1.0;
    return baseEmission * typeMultiplier * (building.grossFloorArea || 10000) / 10000;
  }

  private static calculateEnergyIntensity(building: Building): number {
    // Simplified calculation - in real implementation, use actual energy data
    const baseIntensity = 60;
    const ageMultiplier = building.yearBuilt && building.yearBuilt < 1980 ? 1.5 : 1.0;
    return baseIntensity * ageMultiplier;
  }

  private static detectViolations(
    building: Building, 
    carbonEmissions: number, 
    energyIntensity: number,
    emissionLimit: number
  ): Violation[] {
    const violations: Violation[] = [];

    // Emissions violation
    if (carbonEmissions > emissionLimit) {
      violations.push({
        id: `emissions-${building.id}`,
        type: 'emissions',
        severity: carbonEmissions > emissionLimit * 1.5 ? 'high' : 'medium',
        description: `Carbon emissions exceed Local Law 97 limits by ${((carbonEmissions - emissionLimit) / emissionLimit * 100).toFixed(1)}%`,
        deadline: this.getNextDeadline(new Date().getFullYear()),
        penalty: this.calculateEmissionsPenalty(building, carbonEmissions - emissionLimit),
        requirements: [
          'Reduce carbon emissions through energy efficiency measures',
          'Consider renewable energy installation',
          'Implement building envelope improvements'
        ]
      });
    }

    // Energy efficiency violation
    if (energyIntensity > this.ENERGY_BENCHMARK * 1.3) {
      violations.push({
        id: `efficiency-${building.id}`,
        type: 'efficiency',
        severity: energyIntensity > this.ENERGY_BENCHMARK * 1.6 ? 'high' : 'medium',
        description: `Energy intensity significantly exceeds benchmark (${energyIntensity.toFixed(1)} vs ${this.ENERGY_BENCHMARK} kBtu/sq ft)`,
        deadline: this.getNextDeadline(new Date().getFullYear()),
        requirements: [
          'Conduct energy audit',
          'Upgrade HVAC systems',
          'Improve building insulation',
          'Install energy-efficient lighting'
        ]
      });
    }

    return violations;
  }

  private static calculateComplianceScore(
    building: Building,
    carbonEmissions: number,
    energyIntensity: number,
    emissionLimit: number
  ): number {
    const emissionScore = Math.min(100, (emissionLimit / carbonEmissions) * 100);
    const energyScore = Math.min(100, (this.ENERGY_BENCHMARK / energyIntensity) * 100);
    
    return Math.round((emissionScore + energyScore) / 2);
  }

  private static getNextDeadline(currentYear: number): string {
    if (currentYear < 2024) return '2024-05-01';
    if (currentYear < 2030) return '2030-01-01';
    return '2035-01-01';
  }

  private static calculatePenalty(building: Building, violations: Violation[]): number {
    return violations.reduce((total, violation) => total + (violation.penalty || 0), 0);
  }

  private static calculateEmissionsPenalty(building: Building, excessEmissions: number): number {
    const penaltyRate = 268; // $268 per metric ton CO2e
    const floorArea = building.grossFloorArea || 10000;
    return excessEmissions * floorArea * penaltyRate;
  }

  private static generateRetrofitRecommendations(building: Building, complianceData: ComplianceData): RetrofitRecommendation[] {
    const recommendations: RetrofitRecommendation[] = [];

    // HVAC upgrade recommendation
    if (complianceData.energyIntensity.current > complianceData.energyIntensity.benchmark * 1.2) {
      recommendations.push({
        id: `hvac-${building.id}`,
        title: 'HVAC System Upgrade',
        description: 'Replace aging HVAC equipment with high-efficiency systems',
        category: RetrofitCategory.HVAC,
        priority: 'high',
        estimatedCost: 150000,
        estimatedSavings: 25000,
        paybackPeriod: 6,
        energyReduction: 20,
        emissionReduction: 18,
        implementation: {
          phases: ['Energy audit', 'System design', 'Installation', 'Commissioning'],
          timeline: '6-8 months',
          requirements: ['DOB permits', 'Professional engineer certification']
        }
      });
    }

    // Building envelope recommendation
    if (building.yearBuilt && building.yearBuilt < 1980) {
      recommendations.push({
        id: `envelope-${building.id}`,
        title: 'Building Envelope Improvements',
        description: 'Upgrade insulation, windows, and air sealing',
        category: RetrofitCategory.BUILDING_ENVELOPE,
        priority: 'medium',
        estimatedCost: 80000,
        estimatedSavings: 15000,
        paybackPeriod: 5.3,
        energyReduction: 15,
        emissionReduction: 12,
        implementation: {
          phases: ['Building assessment', 'Design', 'Construction', 'Testing'],
          timeline: '4-6 months',
          requirements: ['DOB permits', 'Energy code compliance']
        }
      });
    }

    // Lighting upgrade recommendation
    recommendations.push({
      id: `lighting-${building.id}`,
      title: 'LED Lighting Upgrade',
      description: 'Replace existing lighting with LED fixtures and controls',
      category: RetrofitCategory.LIGHTING,
      priority: 'low',
      estimatedCost: 30000,
      estimatedSavings: 8000,
      paybackPeriod: 3.8,
      energyReduction: 8,
      emissionReduction: 6,
      implementation: {
        phases: ['Lighting audit', 'Procurement', 'Installation'],
        timeline: '2-3 months',
        requirements: ['Electrical permits']
      }
    });

    return recommendations;
  }

  private static calculatePriority(complianceData: ComplianceData): 'urgent' | 'high' | 'medium' | 'low' {
    const hasHighSeverityViolations = complianceData.violations.some(v => v.severity === 'high');
    const hasEmissionsViolation = complianceData.violations.some(v => v.type === 'emissions');
    const complianceScore = complianceData.complianceScore;

    if (hasHighSeverityViolations && hasEmissionsViolation && complianceScore < 30) {
      return 'urgent';
    } else if (hasEmissionsViolation && complianceScore < 50) {
      return 'high';
    } else if (complianceScore < 70) {
      return 'medium';
    } else {
      return 'low';
    }
  }

  private static calculateFinancials(recommendations: RetrofitRecommendation[]): {
    estimatedCost: number;
    potentialSavings: number;
    paybackPeriod: number;
  } {
    const totalCost = recommendations.reduce((sum, rec) => sum + rec.estimatedCost, 0);
    const totalSavings = recommendations.reduce((sum, rec) => sum + rec.estimatedSavings, 0);
    const paybackPeriod = totalSavings > 0 ? totalCost / totalSavings : 0;

    return {
      estimatedCost: totalCost,
      potentialSavings: totalSavings,
      paybackPeriod: Math.round(paybackPeriod * 10) / 10
    };
  }
}
